import React from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';

import DataOption from '../../models/DataOption';
import StatMessages from '../../StatMessages';
import ChartOption from '../../models/ChartOption';
import getHoursOffset from '../../../utils/getHoursOffset';

const messages = defineMessages({
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
});

export default class Agent {
  constructor(intl) {
    if (!intl) {
      throw Error('Missing intl param');
    }

    const hourOffset = getHoursOffset();

    /**
     * Default callqueuereport api params
     * @type {{object: string, action: string, format: string, range_interval: string, value_offset: string}}
     */
    const apiParams = {
      object: 'agent',
      action: 'read',
      format: 'json',
      range_interval: hourOffset,
      value_offset: hourOffset,
      order: 'sub_fullname',
      queue_name: '*',
      unit: 'seconds',
    };

    const statMessages = new StatMessages(intl);

    /**
     * add socket subscriptions and events if any
     * Example Props
     * stat: 'AC'
     * socketSubscriptions: [{ type: 'agent' }]
     * socketEvents: [{ name: string, adapter: function, adapterParams: object }] }
     * @type {*[]}
     */
    const supportedColumns = [
      // { stat: 'Agent', ...statMessages.messages.Agent }, included as a default so its not included in the selectable columns
      { stat: 'AgentPresence', ...statMessages.messages.AgentPresence },
      { stat: 'AgentStatus', ...statMessages.messages.AgentStatus },
      { stat: 'AH', ...statMessages.messages.AH },
      { stat: 'AHT', ...statMessages.messages.AHT },
      { stat: 'ATT', ...statMessages.messages.ATT },
      { stat: 'CH', ...statMessages.messages.CH },
      { stat: 'IA', ...statMessages.messages.IA },
      { stat: 'IAVG', ...statMessages.messages.IAVG },
      { stat: 'IANS', ...statMessages.messages.IANS },
      { stat: 'IM', ...statMessages.messages.IM },
      { stat: 'MC', ...statMessages.messages.MC },
      { stat: 'OANS', ...statMessages.messages.OANS },
      { stat: 'OM', ...statMessages.messages.OM },
      { stat: 'OATT', ...statMessages.messages.OATT },
      { stat: 'OAvg', ...statMessages.messages.OAvg },
      { stat: 'TT', ...statMessages.messages.TT },
      { stat: 'SMS_VOL', ...statMessages.messages.SMS_VOL },
      { stat: 'SMS_AHT', ...statMessages.messages.SMS_AHT },
    ];

    /**
     * Displays chart options to the user
     * type: timeRange displays an input and sets the start/end for the api request
     * type: queueFilter displays an input and sets the queue/s for the api request
     */
    const chartOptions = [
      new ChartOption({
        type: 'columnList',
        required: true,
        columns: supportedColumns.map((column) => column.stat),
      }),
    ];

    this.dataOptions = new DataOption({
      type: 'api',
      apiParams,
      adapter: 'agentsStatusToTableAdapter',
      baseColumns: ['Agent'],
      chartOptions,
      defaultColumns: ['AgentStatus', 'CH', 'AHT', 'TT'],
      filterCategories: ['callqueue', 'site', 'department', 'subscriber'],
      hasCategories: false,
      hasColumns: true,
      label: <FormattedMessage {...messages.AGENTS} />,
      labelStr: intl.formatMessage({ ...messages.AGENTS }),
      socketEvents: [
        { name: 'agent', adapter: 'agentsStatusSocketToTableAdapter' },
        { name: 'contacts-domain', adapter: 'agentsStatusSocketToTableAdapter' },
      ],
      socketSubscriptions: [
        { type: 'agent', unit: 'seconds' },
        { type: 'contacts', unit: 'seconds' },
      ],
      stat: 'Agent',
      supportedCharts: ['ResponsiveTable'],
      supportedColumns,
    });
  }
}
